import { Info } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SITE_STATUS_OPTIONS } from "../../data";
import {
	emptyOnboardingAddress,
	isOnboardingAddressComplete,
	type SiteData,
	type SiteStatus,
} from "../../types";
import type { StructureViolations } from "../../utils/violations";
import { AddressSection } from "../address-section";
import { CompanyNameField } from "../company-name-field";
import { FieldError } from "../field-error";
import { FieldLabel } from "../field-label";
import { StepShell } from "../step-shell";

interface SiteStepProps {
	initialSite: SiteData | null;
	askCompanyName: boolean;
	initialCompanyName: string;
	violations?: StructureViolations;
	onBack: () => void;
	onContinue: (site: SiteData, companyName: string | null) => void;
}

export function SiteStep({
	initialSite,
	askCompanyName,
	initialCompanyName,
	violations,
	onBack,
	onContinue,
}: SiteStepProps) {
	const [companyName, setCompanyName] = useState(initialCompanyName);
	const [name, setName] = useState(initialSite?.name ?? "");
	const [address, setAddress] = useState(
		initialSite?.address ?? emptyOnboardingAddress(),
	);
	const [area, setArea] = useState(initialSite?.area ?? "");
	const [status, setStatus] = useState<SiteStatus>(
		initialSite?.status ?? "active",
	);

	const canContinue =
		name.trim() !== "" &&
		isOnboardingAddressComplete(address) &&
		(!askCompanyName || companyName.trim() !== "");

	return (
		<StepShell
			title="Cadastre seu canteiro"
			description="O canteiro guarda o endereço físico que vai agrupar suas obras."
			onBack={onBack}
			onContinue={() =>
				onContinue(
					{ name, address, area, status },
					askCompanyName ? companyName.trim() : null,
				)
			}
			continueDisabled={!canContinue}
		>
			{askCompanyName && (
				<CompanyNameField
					value={companyName}
					onChange={setCompanyName}
					error={violations?.company_name}
				/>
			)}

			<div className="onboarding-fields">
				<div className="onboarding-field">
					<FieldLabel>Nome do canteiro</FieldLabel>
					<Input
						placeholder="Ex.: Canteiro Lagoa Nova"
						value={name}
						aria-invalid={!!violations?.name}
						onChange={(event) => setName(event.target.value)}
					/>
					<FieldError message={violations?.name} />
				</div>
				<AddressSection
					value={address}
					onChange={setAddress}
					violations={violations}
					idPrefix="site"
				/>
				<div className="onboarding-field-row">
					<div className="onboarding-field">
						<FieldLabel>Área total (opcional)</FieldLabel>
						<Input
							placeholder="Ex.: 4.200 m²"
							value={area}
							aria-invalid={!!violations?.area}
							onChange={(event) => setArea(event.target.value)}
						/>
						<FieldError message={violations?.area} />
					</div>
					<div className="onboarding-field">
						<FieldLabel>Status inicial</FieldLabel>
						<Select
							value={status}
							onValueChange={(value) => setStatus(value as SiteStatus)}
						>
							<SelectTrigger
								className="onboarding-select"
								aria-invalid={!!violations?.status}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{SITE_STATUS_OPTIONS.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FieldError message={violations?.status} />
					</div>
				</div>
			</div>

			<div className="onboarding-info">
				<Info className="onboarding-info__icon" />
				<p>
					As obras deste canteiro podem ser cadastradas depois, direto no
					painel.
				</p>
			</div>
		</StepShell>
	);
}
